import React, { Fragment, useId, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import sales from '../../Sales';
import { useAdmin } from '../adminContext';
import { amountInput, formatEuro, parseAmount, plural, sortServices } from '../format';
import { useAdminData } from '../store';
import { Button, EntryCard, Field, FormError, ListState, MoneyInput, SearchInput, useFreshIds } from '../ui';

const ServiceForm = ({ service, onDone }) => {
  const isNew = !service;
  const { notify, confirm, changed } = useAdmin();
  const id = useId();
  const [name, setName] = useState(service?.name || '');
  const [price, setPrice] = useState(amountInput(service?.price));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const submit = async () => {
    const clean = name.trim();
    const amount = parseAmount(price);
    if (!clean) {
      setProblem('Vul een naam in.');
      return;
    }
    if (Number.isNaN(amount)) {
      setProblem('Vul een geldige prijs in, of laat het veld leeg.');
      return;
    }

    const body = { name: clean, price: amount === null ? '' : amount };

    setBusy(true);
    setProblem('');
    try {
      if (isNew) {
        await sales.post('products', { ...body, active: 1 });
        notify(`${clean} toegevoegd`);
        setName('');
        setPrice('');
      } else {
        await sales.put('products', { product_id: service.product_id, ...body });
        notify('Dienst opgeslagen');
        onDone();
      }
      changed();
    } catch (error) {
      console.warn(error);
      setProblem('Opslaan is mislukt. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const confirmed = await confirm({
      title: `${service.name} verwijderen?`,
      text: `${service.name} is dan niet meer te kiezen bij een nieuwe deal. Dit kun je niet ongedaan maken.`,
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      await sales.delete(`products?product_id=${service.product_id}`);
      notify(`${service.name} verwijderd`);
      changed();
      onDone();
    } catch (error) {
      console.warn(error);
      notify('Verwijderen is mislukt', 'error');
      setBusy(false);
    }
  };

  return (
    <EntryCard
      title={isNew ? 'Nieuwe dienst' : 'Dienst bewerken'}
      inline={!isNew}
      onSubmit={submit}
      onCancel={onDone}
      busy={busy}
    >
      <div className="adm-inline">
        <Field label="Naam" htmlFor={`${id}-name`} className="adm-grow">
          <input
            id={`${id}-name`}
            className="adm-input"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Bijvoorbeeld Managed Cloud Backup"
          />
        </Field>
        <Field label="Vaste prijs, mag leeg" htmlFor={`${id}-price`} className="adm-w-160">
          <MoneyInput
            id={`${id}-price`}
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            placeholder="Geen"
          />
        </Field>
        {!isNew && (
          <Button variant="danger-text" icon={Trash2} onClick={remove} disabled={busy}>
            Verwijderen
          </Button>
        )}
        {!isNew && (
          <Button variant="ghost" onClick={onDone} disabled={busy}>
            Annuleren
          </Button>
        )}
        <Button type="submit" icon={isNew ? Plus : undefined} disabled={busy}>
          {busy ? 'Bezig...' : isNew ? 'Toevoegen' : 'Opslaan'}
        </Button>
      </div>
      <FormError>{problem}</FormError>
    </EntryCard>
  );
};

const Services = () => {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const { data, loading, error, reload } = useAdminData({ products: 'products?filter=true' });

  const rows = useMemo(() => (data ? sortServices(data.products) : []), [data]);
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? rows.filter((row) => (row.name || '').toLowerCase().includes(needle)) : rows;
  }, [rows, query]);

  const fresh = useFreshIds(rows, (row) => row.product_id);

  return (
    <>
      <ServiceForm onDone={() => {}} />
      <section className="adm-panel">
        <div className="adm-panel-head">
          <h2 className="adm-panel-title">Diensten</h2>
          {data && <span className="adm-count num">{plural(rows.length, 'dienst', 'diensten')}</span>}
          <SearchInput value={query} onChange={setQuery} placeholder="Zoek een dienst" />
        </div>
        <ListState
          loading={loading && !data}
          error={error && !data}
          onRetry={reload}
          empty={shown.length === 0 && (query ? 'Geen dienst gevonden.' : 'Nog geen diensten.')}
        >
          <div className="adm-rows">
            {shown.map((row) => {
              const open = editing === row.product_id;
              return (
                <Fragment key={row.product_id}>
                  <button
                    type="button"
                    className={`adm-row adm-row-simple${open ? ' is-open' : ''}${fresh.has(row.product_id) ? ' is-fresh' : ''}`}
                    aria-expanded={open}
                    onClick={() => setEditing(open ? null : row.product_id)}
                  >
                    <span className="adm-row-main">
                      <b>{row.name}</b>
                    </span>
                    <span className="adm-row-meta num">{row.price ? formatEuro(row.price) : 'Geen vaste prijs'}</span>
                    <Pencil size={15} className="adm-row-edit" aria-label="Bewerken" />
                  </button>
                  {open && (
                    <div className="adm-row-editor">
                      <ServiceForm service={row} onDone={() => setEditing(null)} />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </ListState>
      </section>
    </>
  );
};

export default Services;
